import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const CARDS = [
  {
    id: 'prints',
    href: 'https://print.notescrafter.com/order/upload',
    label: 'PRINTS',
    sub: 'Upload your PDF, choose print specs, and let us handle the rest. We create premium notebooks and deliver them to your doorstep – made for Indian students. 🇮🇳',
    ctaText: 'ORDER NOW',
  },
  {
    id: 'flow',
    href: 'https://flow.notescrafter.com',
    label: 'FLOW',
    sub: 'An intelligent multi-step workflow designed to enhance dark lecture slides into perfect printable notes, create study-ready notes in seconds.',
    ctaText: 'START PROCESSING',
  },
  {
    id: 'tools',
    href: 'https://tools.notescrafter.com',
    label: 'TOOLS',
    sub: 'Quick PDF & Study Tools to optimize your study material. Launch any of our 10+ free tools directly to merge, split, compress, or convert documents.',
    ctaText: 'OPEN TOOLS',
  },
  {
    id: 'creators',
    href: 'https://creators.notescrafter.com',
    label: 'CREATORS',
    sub: 'Join NotesCrafter Creators, share your unique code, give your audience 5% off, and earn 5% commission on every successful order.',
    ctaText: 'START EARNING',
  },
  {
    id: 'pass',
    href: '/pricing',
    label: 'COMMUNITY PASS',
    sub: 'One pass, multiple benefits ~ enjoy unlimited processing and exclusive discounted printing rates, designed to make your study journey easier and more affordable.',
    ctaText: 'GET COMMUNITY PASS',
  },
] as const;

const N = CARDS.length;
const EASE: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

// Taller concave path to fit all content (300 width x 280 height)
const CARD_PATH = [
  'M 0 22',
  'A 22 22 0 0 1 22 0',
  'Q 150 46 278 0',
  'A 22 22 0 0 1 300 22',
  'L 300 258',
  'A 22 22 0 0 1 278 280',
  'L 22 280',
  'A 22 22 0 0 1 0 258',
  'Z',
].join(' ');

const ProductCarouselSection: React.FC = () => {
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const [width, setWidth] = useState(1200);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setWidth(window.innerWidth);

    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setTick(p => p + 1), 5000);
    return () => clearInterval(t);
  }, [paused]);

  const active = ((tick % N) + N) % N;

  const jump = (idx: number) => {
    const raw = idx - active;
    const diff = ((raw + Math.floor(N / 2)) % N) - Math.floor(N / 2);
    setTick(p => p + diff);
  };

  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;

  // Card scales: Center (1.0), Side Neighbors (0.85), Outer (0.72)
  const scaleCenter = 1.0;
  const scaleSide = isMobile ? 1.0 : 0.85;

  // Base card widths: Full width minus padding on mobile
  const cardWidth = isMobile ? Math.min(width - 32, 400) : isTablet ? 230 : 280;

  // No overlap: add a positive gap (e.g. 10px on mobile, 18px on desktop)
  const gap = isMobile ? 10 : 18;

  // Distance between centers = (half of center card + half of side card) + gap. On mobile, shift by full viewport width.
  const xOffset = isMobile ? width : (cardWidth * 0.5 * scaleCenter + cardWidth * 0.5 * scaleSide) + gap;

  const getXOffset = (rel: number) => {
    return rel * xOffset;
  };

  const getScale = (rel: number) => {
    if (rel === 0) return scaleCenter;
    if (Math.abs(rel) === 1) return scaleSide;
    return isMobile ? 1.0 : 0.72;
  };

  const getZIndex = (rel: number) => {
    if (rel === 0) return 30;
    if (Math.abs(rel) === 1) return 20;
    return 10;
  };

  return (
    <section
      style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        paddingTop: isMobile ? '0px' : '12px',
      }}
    >
      {/* ── Brand purple wave (full bleed edge-to-edge) ── */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: '62%', zIndex: 0, pointerEvents: 'none',
          margin: 0, padding: 0,
        }}
      >
        <svg
          viewBox="0 0 1440 140"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '96px', display: 'block', margin: 0, padding: 0 }}
        >
          <path
            d="M0 88 C140 10 320 118 560 58 C760 5 960 110 1160 55 C1320 12 1410 84 1440 72 L1440 140 L0 140 Z"
            fill="rgba(50,12,110,0.22)"
          />
          <path
            d="M0 104 C180 32 380 122 620 72 C840 24 1060 118 1280 68 C1390 38 1432 98 1440 90 L1440 140 L0 140 Z"
            fill="rgba(46,8,100,0.32)"
          />
        </svg>
        <div style={{ background: 'rgba(46,8,100,0.32)', height: 'calc(100% - 95px)', margin: 0 }} />
      </div>

      {/* ── Card stage ── */}
      <div
        style={{
          position: 'relative', height: isMobile ? '255px' : '370px',
          overflow: 'hidden', zIndex: 10,
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {CARDS.map((card, i) => {
          // Seamless loop via virtual-index trick
          const k = Math.round((tick - i) / N);
          const vIdx = i + N * k;
          const rel = vIdx - tick; // range [-2, 2] for 5 cards
          const isCenter = rel === 0;

          if (Math.abs(rel) > 2) return null;

          return (
            <motion.a
              key={card.id}
              href={card.href}
              target="_blank"
              rel="noopener noreferrer"
              animate={{
                x: getXOffset(rel),
                scale: getScale(rel),
                zIndex: getZIndex(rel),
              }}
              transition={{ duration: 0.62, ease: EASE }}
              onClick={(e) => {
                if (!isCenter) {
                  e.preventDefault();
                  jump(i);
                }
              }}
              style={{
                position: 'absolute',
                left: 0, right: 0,
                marginLeft: 'auto', marginRight: 'auto',
                bottom: 0,
                width: `${cardWidth}px`,
                paddingTop: isMobile ? '44px' : '54px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* ── Floating circle badge with brand logo inside (Bigger) ── */}
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  display: 'flex', justifyContent: 'center', zIndex: 30,
                }}
              >
                <motion.div
                  animate={isCenter ? { y: [0, -6, 0] } : { y: 0 }}
                  transition={isCenter
                    ? { duration: 3, repeat: Infinity, ease: 'easeInOut', repeatType: 'mirror' }
                    : {}}
                  style={{
                    width: isCenter ? (isMobile ? '88px' : '110px') : (isMobile ? '72px' : '90px'),
                    height: isCenter ? (isMobile ? '88px' : '110px') : (isMobile ? '72px' : '90px'),
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    border: `3px solid rgba(139,92,246,${isCenter ? '0.75' : '0.45'})`,
                    boxShadow: isCenter
                      ? '0 12px 36px rgba(139,92,246,0.45), 0 0 0 6px rgba(139,92,246,0.12)'
                      : '0 4px 16px rgba(139,92,246,0.22)',
                    background: 'radial-gradient(circle at center, rgba(30,12,68,0.95) 0%, rgba(10,4,22,0.98) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'width .5s ease, height .5s ease, box-shadow .5s ease, border-color .5s ease',
                  }}
                >
                  <img
                    src="/logo.v2.png"
                    alt="NotesCrafter Logo"
                    style={{
                      height: isCenter ? (isMobile ? '36px' : '46px') : (isMobile ? '28px' : '36px'),
                      width: 'auto',
                      filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.7))',
                      transition: 'height .5s ease',
                    }}
                  />
                </motion.div>
              </div>

              {/* ── Card body (Unmuted side cards, smoky glass & opaque) ── */}
              <div
                style={{
                  position: 'relative', flex: 1,
                  filter: `drop-shadow(0 ${isCenter ? '24px 50px' : '12px 24px'} rgba(0,0,0,${isCenter ? '0.7' : '0.48'}))`,
                  transition: 'filter .5s ease',
                }}
              >
                {/* Concave-top SVG card shape with Glass Blur Smoke Opaque Layer */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '22px',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    zIndex: 0,
                  }}
                />
                
                <svg
                  viewBox="0 0 300 280"
                  preserveAspectRatio="none"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', zIndex: 1 }}
                >
                  <defs>
                    <linearGradient id={`g-${card.id}`} x1="10%" y1="0%" x2="90%" y2="100%">
                      <stop offset="0%" stopColor={isCenter ? 'rgba(50,15,110,0.96)' : 'rgba(38,10,85,0.94)'} />
                      <stop offset="100%" stopColor="rgba(8,2,24,0.99)" />
                    </linearGradient>
                  </defs>
                  <path
                    d={CARD_PATH}
                    fill={`url(#g-${card.id})`}
                    stroke={`rgba(139,92,246,${isCenter ? '0.45' : '0.35'})`}
                    strokeWidth="1.5"
                  />
                </svg>

                {/* Card content */}
                <div
                  style={{
                    position: 'relative', zIndex: 10,
                    padding: isMobile ? '46px 20px 18px' : '68px 22px 26px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Label Header (Logo Removed, Centered Label Only) */}
                  <div
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: isMobile ? '8px' : '14px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 900,
                        color: '#ffffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.22em',
                        fontSize: isCenter ? (isMobile ? '12px' : '16px') : (isMobile ? '10px' : '13px'),
                        lineHeight: 1,
                        textShadow: '0 2px 10px rgba(168,85,247,0.4)',
                        transition: 'font-size .45s ease',
                      }}
                    >
                      {card.label}
                    </span>
                  </div>

                  {/* Subheading description */}
                  <p
                    style={{
                      color: 'rgba(209,213,219,0.95)', // Highly legible white-grey
                      fontSize: isMobile ? '9.5px' : '11.5px',
                      lineHeight: 1.55,
                      margin: 0,
                      minHeight: isMobile ? '44px' : '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {card.sub}
                  </p>

                  {/* Center Card CTA Button (Width matches content inside, with external link icon) */}
                  {isCenter && (
                    <div style={{ marginTop: isMobile ? '12px' : '18px', width: '100%' }}>
                      <div
                        style={{
                          width: '100%',
                          padding: isMobile ? '8px 12px' : '11px 18px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          boxShadow: '0 4px 18px rgba(139, 92, 246, 0.45)',
                          color: '#ffffff',
                          fontSize: isMobile ? '9.5px' : '12px',
                          fontWeight: 900,
                          textTransform: 'uppercase',
                          letterSpacing: '0.09em',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          border: '1px solid rgba(255,255,255,0.15)',
                        }}
                      >
                        <span>{card.ctaText}</span>
                        <svg
                          style={{ width: isMobile ? '11px' : '14px', height: isMobile ? '11px' : '14px', flexShrink: 0 }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.a>
          );
        })}
      </div>

      {/* ── Pagination dots ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          paddingTop: '16px',
          paddingBottom: '24px',
        }}
      >
        {CARDS.map((_, idx) => {
          const isActive = idx === active;
          return (
            <button
              key={idx}
              onClick={() => jump(idx)}
              onTouchStart={() => jump(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                pointerEvents: 'auto',
              }}
            >
              <div
                style={{
                  width: isActive ? '10px' : '8px',
                  height: isActive ? '10px' : '8px',
                  borderRadius: '50%',
                  background: isActive ? '#8b5cf6' : 'transparent',
                  border: `2px solid ${isActive ? '#8b5cf6' : 'rgba(139,92,246,0.55)'}`,
                  boxShadow: isActive ? '0 0 0 3px rgba(139,92,246,0.25)' : 'none',
                  transition: 'all 0.35s ease',
                }}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ProductCarouselSection;
